import React, { useRef, useState } from 'react';

const SCREENS = [
  {
    id: 'dashboard',
    label: 'Home dashboard',
    url: 'app.phennx.io/home',
    title: 'The day, already triaged',
    copy:
      'Overdue, due today, unread, and mentions up front — with the personal work queue, today’s visit schedule, and live activity in one view.',
    img: '/product/home-dashboard.webp',
    alt: 'Phennx home dashboard with overdue and due-today counters, personal work queue, and visit schedule',
  },
  {
    id: 'startup',
    label: 'Study startup',
    url: 'app.phennx.io/study-startup',
    title: 'Startup pipelines with a pulse',
    copy:
      'Every study moving toward first-patient-in — sponsor, indication, phase, and checklist progress in one board instead of a shared spreadsheet.',
    img: '/product/study-startup.webp',
    alt: 'Phennx study startup board listing studies with sponsor, indication, phase, and progress',
  },
  {
    id: 'monitor',
    label: 'Monitor workspace',
    url: 'app.phennx.io/monitor/assignments',
    title: 'Assignment-scoped monitoring',
    copy:
      'Monitors see exactly the studies and sites they are assigned to — visits, statuses, and notes without over-exposing the rest of the tenant.',
    img: '/product/monitor-workspace.webp',
    alt: 'Phennx monitor workspace listing monitoring visit assignments with statuses and notes',
  },
  {
    id: 'documents',
    label: 'Documents',
    url: 'app.phennx.io/documents',
    title: 'Documents that carry their history',
    copy:
      'Shared documents with versioning, review queues, and signatures — the current copy and the paper trail behind it stay together.',
    img: '/product/documents-workspace.webp',
    alt: 'Phennx shared documents workspace with folders, versions, and signature status',
  },
  {
    id: 'qc',
    label: 'Quality control',
    url: 'app.phennx.io/qc/instances',
    title: 'QC as a queue, not a fire drill',
    copy:
      'Every check has an owner, a status, and a timeline — so data issues surface as routed work instead of surprises at monitoring visits.',
    img: '/product/quality-control.webp',
    alt: 'Phennx quality control queue listing checks per patient visit with owners, statuses, and timelines',
  },
  {
    id: 'reports',
    label: 'Reports',
    url: 'app.phennx.io/reports',
    title: 'Reporting that reads the record',
    copy:
      'Operational and study reports draw from the same data the team works in daily — no exports, no reconciliation, no second source of truth.',
    img: '/product/reports.webp',
    alt: 'Phennx reports workspace with report categories and generated outputs',
  },
];

export default function ProductTourSection() {
  const [activeId, setActiveId] = useState(SCREENS[0].id);
  const tabRefs = useRef({});

  const activeScreen = SCREENS.find((screen) => screen.id === activeId) ?? SCREENS[0];

  const handleTabKeyDown = (event, index) => {
    let nextIndex = null;
    if (event.key === 'ArrowRight') nextIndex = (index + 1) % SCREENS.length;
    if (event.key === 'ArrowLeft') nextIndex = (index - 1 + SCREENS.length) % SCREENS.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = SCREENS.length - 1;
    if (nextIndex === null) return;

    event.preventDefault();
    const next = SCREENS[nextIndex];
    setActiveId(next.id);
    tabRefs.current[next.id]?.focus();
  };

  return (
    <section className="hx-tour" id="product" aria-labelledby="product-heading">
      <div className="hx-container">
        <div className="hx-section-head">
          <p className="hx-eyebrow">
            <span className="hx-eyebrow__tick" aria-hidden="true" />
            Real product, real screens
          </p>
          <h2 className="hx-h2" id="product-heading">
            No mockups. This is the product.
          </h2>
          <p className="hx-lead">
            Screens straight from the Phennx user guide — the same workspaces sites, sponsors, and
            monitors work in every day.
          </p>
        </div>

        <div className="hx-tour__tabs" role="tablist" aria-label="Product workspaces">
          {SCREENS.map((screen, index) => (
            <button
              key={screen.id}
              ref={(node) => {
                tabRefs.current[screen.id] = node;
              }}
              type="button"
              role="tab"
              id={`tour-tab-${screen.id}`}
              aria-selected={screen.id === activeId}
              aria-controls={`tour-panel-${screen.id}`}
              tabIndex={screen.id === activeId ? 0 : -1}
              className={`hx-tour__tab ${screen.id === activeId ? 'is-active' : ''}`}
              onClick={() => setActiveId(screen.id)}
              onKeyDown={(event) => handleTabKeyDown(event, index)}
            >
              {screen.label}
            </button>
          ))}
        </div>

        <div
          className="hx-tour__panel"
          role="tabpanel"
          id={`tour-panel-${activeScreen.id}`}
          aria-labelledby={`tour-tab-${activeScreen.id}`}
        >
          <div className="hx-browser">
            <div className="hx-browser__bar" aria-hidden="true">
              <span className="hx-browser__dot" />
              <span className="hx-browser__dot" />
              <span className="hx-browser__dot" />
              <span className="hx-browser__url">{activeScreen.url}</span>
            </div>
            <img
              className="hx-browser__screen"
              src={activeScreen.img}
              alt={activeScreen.alt}
              width="1600"
              height="1200"
              loading="lazy"
            />
          </div>

          <div className="hx-tour__caption">
            <h3 className="hx-tour__caption-title">{activeScreen.title}</h3>
            <p className="hx-tour__caption-copy">{activeScreen.copy}</p>
          </div>
        </div>
      </div>
    </section>
  );
}
